/** Measured natural widths of the bar's slots, and the notch they sit around. */
export interface NotchRowWidths {
  left: number
  center: number
  right: number
  notch: number
}

/**
 * Offsets from the notch's center line. Both lists are placed by their left edge, so content grows in
 * reading order: the left side's toward the notch from its outer edge, the right side's outward from
 * the notch. Content already shown stays put while the edge springs.
 */
export interface NotchRowLayout {
  leftStart: number
  rightStart: number
  center: { x: number; width: number }
}

function measureCenterHalf(widths: NotchRowWidths): number {
  return Math.max(widths.notch, widths.center) / 2
}

/** The half-width the bar's own content needs. Both sides take the wider side's width. */
export function measureRowExtent(widths: NotchRowWidths, isCenteredRow: boolean): number {
  if (isCenteredRow) {
    return (widths.left + widths.center + widths.right) / 2
  }
  return measureCenterHalf(widths) + Math.max(widths.left, widths.right)
}

/**
 * Each side owns the half of the cutout beside the center, and its content reaches the outer edge,
 * so spare space sits against the notch. A centered row ignores the extent and keeps its run
 * centered, leaving any spare space at both outer edges.
 */
export function layoutNotchRow(widths: NotchRowWidths, extent: number, isCenteredRow: boolean): NotchRowLayout {
  if (isCenteredRow) {
    const start = -measureRowExtent(widths, true)
    return {
      leftStart: start,
      rightStart: start + widths.left + widths.center,
      center: { x: start + widths.left, width: widths.center },
    }
  }

  const centerHalf = measureCenterHalf(widths)
  return {
    leftStart: -extent,
    rightStart: Math.max(extent - widths.right, centerHalf),
    center: { x: -centerHalf, width: centerHalf * 2 },
  }
}
